import { useMutation } from '@tanstack/react-query';
import { OverlappingSourcingPurchaseResponse } from 'api-clients/monolith/models/OverlappingSourcingPurchaseResponse';
import _uniq from 'lodash/uniq';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { validateSourcingPurchases } from '@/api-clients/validate-sourcing-purchases';
import { useToaster } from '@/components/base/toaster';
import { useFlag } from '@/flags';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { ZIPRECRUITER } from '@/types/channels';

import { BudgetAllocation, BudgetType, ChannelFormState } from '../types';
import { ZIPRECRUITER_BUDGET_PRESETS } from '../validation/constants';
import {
  allocateBudgetEvenly,
  calculateTotalBudget,
  computeChannelBudgets,
} from '../utils/channel-utils';
import {
  parseVonqPostingIssues,
  VonqOpeningIssue,
} from '../utils/vonq-posting-issues-parser';

interface UseChannelFormProps {
  /** Form-state key (channel slug or, for Vonq, product ID). */
  channelKey: string;
  snapshotBeforeEditRef: React.MutableRefObject<ChannelFormState>;
}

export function useChannelForm({
  channelKey,
  snapshotBeforeEditRef,
}: UseChannelFormProps) {
  const intl = useIntl();
  const isAsyncPostingEnabled = useFlag('hire-source-async-job-posting');
  const { notifyError, notifySuccess } = useToaster();
  const { hookForm, selectedChannelKeys, setSelectedOpeningIds } =
    useJobPostingContext();
  const {
    watch,
    setValue,
    getValues,
    trigger,
    setError,
    clearErrors,
    resetField,
  } = hookForm;
  // Array of opening IDs that have conflicts for a specific channel
  const [openingsWithConflicts, setOpeningsWithConflicts] =
    useState<string[]>();
  const [vonqPostingIssues, setVonqPostingIssues] = useState<
    VonqOpeningIssue[]
  >([]);

  const channelBudget = watch(`channels.${channelKey}.channelBudget`);
  const startsAt = watch(`channels.${channelKey}.startsAt`);
  const endAt = watch(`channels.${channelKey}.endAt`);
  const budgetType = watch(`channels.${channelKey}.budgetType`);
  const openingBudgets = watch(`channels.${channelKey}.openingBudgets`);

  /* Sync totalChannelBudget when channelBudget, budgetType, or dates change */
  useEffect(() => {
    setValue(
      `channels.${channelKey}.totalChannelBudget`,
      calculateTotalBudget({
        budgetType,
        budgetPerUnit: channelBudget ?? 0,
        startDate: startsAt,
        endDate: endAt,
      }),
    );
  }, [budgetType, channelBudget, startsAt, endAt, setValue, channelKey]);

  /* Validate the channel budget by triggering the channel budget validation */
  const validateChannelBudget = () => {
    void trigger(`channels.${channelKey}.channelBudget`);
  };

  const distributeOpeningBudgets = () => {
    const { channelBudget, budgetAllocation, openingBudgets } = getValues(
      `channels.${channelKey}`,
    );
    if (channelBudget && budgetAllocation === 'evenly') {
      setValue(
        `channels.${channelKey}.openingBudgets`,
        allocateBudgetEvenly(openingBudgets, channelBudget),
      );
    } else if (channelBudget && budgetAllocation === 'per-funnel') {
      void trigger(`channels.${channelKey}.openingBudgets`);
    }
  };

  const handleChannelBudgetChange = () => {
    distributeOpeningBudgets();
    validateChannelBudget();
  };

  const handleBudgetAllocationChange = (allocation: BudgetAllocation) => {
    const channel = getValues(`channels.${channelKey}`);
    if (channel.slug === ZIPRECRUITER && allocation === 'evenly') {
      const budget =
        Object.values(channel.openingBudgets).at(-1) ??
        ZIPRECRUITER_BUDGET_PRESETS[0].value;
      setValue(
        `channels.${channelKey}.channelBudget`,
        budget * Object.keys(channel.openingBudgets).length,
      );
    }
    distributeOpeningBudgets();
    validateChannelBudget();
  };

  const conflictsErrorMessage = useCallback(
    (count: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            '{count, plural, one {1 opening} other {{count} openings}} have campaigns that overlap.',
          description: 'Error message for overlapping sourcing purchases',
        },
        { count },
      ),
    [intl],
  );

  /**
   * Mutation to validate the overlapping purchases for the channel and set the conflicts state.
   */
  const {
    mutate: validateOverlappingPurchases,
    isPending,
    data,
  } = useMutation({
    mutationFn: validateSourcingPurchases,
    onSuccess: (data: OverlappingSourcingPurchaseResponse) => {
      const { conflicts, vonq_posting_issues: vonqPostingIssuesData } = data;
      const uniqueOpeningsWithConflicts = _uniq(
        conflicts.map(conflict => conflict.opening_id),
      );

      const parsedIssues = parseVonqPostingIssues(vonqPostingIssuesData);
      setVonqPostingIssues(parsedIssues);

      const hasBlockingIssues =
        uniqueOpeningsWithConflicts.length > 0 || parsedIssues.length > 0;

      if (uniqueOpeningsWithConflicts.length > 0) {
        setOpeningsWithConflicts(uniqueOpeningsWithConflicts);

        notifyError({
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
        setError(`channels.${channelKey}.endAt`, {
          type: 'overlap',
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
      }

      if (parsedIssues.length > 0) {
        notifyError({
          message: intl.formatMessage({
            defaultMessage:
              'Some openings have posting issues that need to be resolved.',
            description:
              'Toast error when Vonq validation returns field-level issues for openings',
          }),
        });
      }

      if (!hasBlockingIssues) {
        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Changes saved successfully',
            description: 'Success message for saving campaign details',
          }),
        });
        setValue(`channels.${channelKey}.isExpanded`, false);
        setValue(`channels.${channelKey}.isValidated`, true);

        const ref = snapshotBeforeEditRef;
        ref.current = JSON.parse(
          JSON.stringify(getValues(`channels.${channelKey}`)),
        ) as ChannelFormState;

        resetField(`channels.${channelKey}`, {
          defaultValue: getValues(`channels.${channelKey}`),
          keepDirty: false,
        });
      }
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to save changes',
          description: 'Error message for saving campaign details',
        }),
      });
    },
  });

  const conflicts: OverlappingSourcingPurchaseResponse['conflicts'] =
    useMemo(() => {
      const allConflicts = data?.conflicts ?? [];
      // Filter conflicts to only include openings that are still in openingsWithConflicts. We render the conflicts from API and use openingsWithConflicts to manage the state.
      if (openingsWithConflicts) {
        return allConflicts.filter(conflict =>
          openingsWithConflicts.includes(conflict.opening_id),
        );
      }
      return allConflicts;
    }, [data, openingsWithConflicts]);

  /* Validate the channel changes by calling the API to validate if there are any overlapping purchases */
  const validateChannelChanges = async () => {
    const isValid = await trigger(`channels.${channelKey}`);
    if (!isValid) return;

    const channelValues = getValues(`channels.${channelKey}`);
    const vonqDetails = channelValues.vonqDetails;
    validateOverlappingPurchases({
      channel_slug: channelValues.slug,
      start_date: channelValues.startsAt,
      end_date: channelValues.endAt,
      opening_ids: Object.keys(channelValues.openingBudgets ?? {}),
      is_vonq_channel: !!vonqDetails,
      vonq_product_id: vonqDetails?.productId ?? null,
      remote_channel_id:
        vonqDetails?.channelId != null ? String(vonqDetails.channelId) : null,
      vonq_contract_id: vonqDetails?.contractId ?? null,
    });
  };

  /* Cancel the channel changes by resetting the form to the saved snapshot */
  const cancelChannelChanges = () => {
    void trigger(`channels.${channelKey}`);
    // Reset the form to the saved snapshot
    setValue(`channels.${channelKey}`, {
      ...snapshotBeforeEditRef.current,
      isExpanded: false,
    });
  };

  /* Remove the channel from the posting by removing the channel from the form data. */
  const removeChannelFromPosting = () => {
    const formData = getValues();
    const {
      channels: { [channelKey]: _, ...restChannels },
    } = formData;
    setValue('channels', restChannels);
  };

  /* Recalculate channelBudget + totalChannelBudget from opening budgets */
  const recalculateChannelBudget = (
    openings?: Record<string, number | null>,
  ) => {
    const resolved =
      openings ?? getValues(`channels.${channelKey}.openingBudgets`);
    const { channelBudget: newBudget, totalChannelBudget } =
      computeChannelBudgets({
        openingBudgets: resolved,
        budgetType,
        startsAt,
        endAt,
      });
    setValue(`channels.${channelKey}.channelBudget`, newBudget);
    setValue(`channels.${channelKey}.totalChannelBudget`, totalChannelBudget);
    distributeOpeningBudgets();
    validateChannelBudget();
  };

  /* Remove the opening from the channel when user deletes it */
  const removeOpeningFromChannel = (openingId: string) => {
    const updatedOpenings = { ...openingBudgets };
    delete updatedOpenings[openingId];
    setValue(`channels.${channelKey}.openingBudgets`, updatedOpenings);
    recalculateChannelBudget(updatedOpenings);

    // Pool posts one campaign per openingMessages entry, so drop it there too.
    const openingMessages = getValues(
      `channels.${channelKey}.poolDetails.openingMessages`,
    );
    if (openingMessages && openingId in openingMessages) {
      const { [openingId]: _removed, ...remainingMessages } = openingMessages;
      setValue(
        `channels.${channelKey}.poolDetails.openingMessages`,
        remainingMessages,
      );
    }

    // If there is only one opening left, set the budget allocation to evenly
    if (Object.keys(updatedOpenings).length === 1)
      setValue(`channels.${channelKey}.budgetAllocation`, 'evenly');

    // If there is only one form entry left, remove the opening from the list of selected openings.
    if (selectedChannelKeys.length === 1)
      setSelectedOpeningIds(prev => prev.filter(id => id !== openingId));
  };

  const updateConflicts = useCallback(
    (updatedConflicts: string[] | undefined) => {
      setOpeningsWithConflicts(updatedConflicts);

      if (updatedConflicts && updatedConflicts.length > 0) {
        setError(`channels.${channelKey}.endAt`, {
          type: 'overlap',
          message: conflictsErrorMessage(updatedConflicts?.length ?? 0),
        });
      } else {
        clearErrors(`channels.${channelKey}.endAt`);
      }
    },
    [channelKey, clearErrors, conflictsErrorMessage, setError],
  );

  useEffect(() => {
    if (!isAsyncPostingEnabled) return;
    // The openings rail writes the form without using this hook's removal handlers.
    const subscription = watch((values, { name }) => {
      if (
        ![
          'channels',
          `channels.${channelKey}`,
          `channels.${channelKey}.openingBudgets`,
        ].includes(name ?? '')
      )
        return;
      if (!openingsWithConflicts?.length) return;
      const selected = values.channels?.[channelKey]?.openingBudgets ?? {};
      const remaining = openingsWithConflicts.filter(id =>
        Object.hasOwn(selected, id),
      );
      if (remaining.length !== openingsWithConflicts.length)
        updateConflicts(remaining);
    });
    return () => subscription.unsubscribe();
  }, [
    channelKey,
    isAsyncPostingEnabled,
    openingsWithConflicts,
    updateConflicts,
    watch,
  ]);

  /* Remove the conflicting opening from the channel when user deletes it */
  const removeConflictingOpening = (openingId: string) => {
    removeOpeningFromChannel(openingId);
    if (!isAsyncPostingEnabled) {
      updateConflicts(openingsWithConflicts?.filter(id => id !== openingId));
    }
  };

  /* Remove an opening that has posting issues from the channel */
  const removeOpeningWithIssues = (openingId: string) => {
    setVonqPostingIssues(prev => prev.filter(i => i.openingId !== openingId));
    removeOpeningFromChannel(openingId);
  };

  const clearConflicts = () => {
    setOpeningsWithConflicts(undefined);
  };

  /* Recalculate the total budget when the budget type changes */
  const handleBudgetTypeChange = (newBudgetType: BudgetType) => {
    setValue(
      `channels.${channelKey}.totalChannelBudget`,
      calculateTotalBudget({
        budgetType: newBudgetType,
        budgetPerUnit: channelBudget ?? 0,
        startDate: startsAt,
        endDate: endAt,
      }),
    );
    validateChannelBudget();
  };

  return {
    isPending,
    validateChannelChanges,
    cancelChannelChanges,
    removeChannelFromPosting,
    removeOpeningFromChannel,
    removeConflictingOpening,
    removeOpeningWithIssues,
    validateChannelBudget,
    recalculateChannelBudget,
    conflicts,
    openingsWithConflicts,
    clearConflicts,
    handleBudgetTypeChange,
    handleChannelBudgetChange,
    handleBudgetAllocationChange,
    vonqPostingIssues,
  };
}
